// components/features/support/calls/CallForm.tsx
import { useState, useEffect, useMemo } from "react";
import { X, Plus, UserPlus } from "lucide-react";
import { Call } from "../../../../types/features/support/Call";
import { User } from "../../../../types/features/administration/User";
import useUsers from "../../../../hooks/administration/users/useUsers";
import ClientCreateModal from "../clients/ClientCreateModal";
import useAuth from "../../../../hooks/auth/useAuth";
import { alertError } from "../../../../helpers/alertError";
import { countriesService } from "../../../../services/administration/countriesService";

// Liste des catégories pour le ticket
const problemCategoriesList = [
  { name: "Inscription", slug: "inscription" },
  { name: "Connexion", slug: "connexion" },
  { name: "OTP", slug: "otp" },
  { name: "KYC", slug: "kyc" },
  { name: "Wallet", slug: "wallet" },
  { name: "Transactions", slug: "transactions" },
  { name: "Retrait", slug: "retrait" },
  { name: "Dépôt", slug: "depot" },
  { name: "Buy & Sell", slug: "buy-sell" },
  { name: "Transfert bancaire", slug: "transfert-bancaire" },
  { name: "Sécurité", slug: "securite" },
  { name: "Technique", slug: "technique" },
  { name: "Notifications", slug: "notifications" },
  { name: "Parrainage", slug: "parrainage" },
  { name: "Autre", slug: "autre" },
];

interface CallFormProps {
  initialData?: Call;
  onSubmit: (data: Omit<Call, "id" | "created_at" | "updated_at">) => Promise<void>;
  isLoading?: boolean;
}

export default function CallForm({ initialData, onSubmit, isLoading = false }: CallFormProps) {
  const { users, usersLoading } = useUsers({ autoFetch: true });
  const { user: currentUser } = useAuth();

  const [formData, setFormData] = useState({
    phone: "", // ← Ce champ est maintenant rempli via le client lié
    direction: "entrant" as "entrant" | "sortant",
    duration: 0,
    tags: [] as string[],
    notes: "",
    tagInput: "",

    // Client lié
    client_id: "",
    client_name: "",
    client_phone: "",

    // Pour un user existant
    user_id: "",
    user_phone: "",
    firstname: "",
    lastname: "",
    email: "",
    country: "",
    country_code: "",

    // Pour un nouveau client (prospect)
    isNewClient: false,
  });

  // Données pour le ticket automatique
  const [ticketData, setTicketData] = useState({
    solution: "",
    problem_categories: [] as string[],
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const queryLower = searchQuery.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
      const email = (user.email || "").toLowerCase();
      const phone = user.phonenumber || "";
      return fullName.includes(queryLower) || email.includes(queryLower) || phone.includes(searchQuery);
    });
  }, [users, searchQuery]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        phone: initialData.client_phone || "",
        direction: initialData.direction,
        duration: initialData.duration,
        tags: initialData.tags || [],
        notes: initialData.notes || "",
        tagInput: "",
        client_id: initialData.client_id || "",
        client_name: initialData.client_name || "",
        client_phone: initialData.client_phone || "",
        user_id: "",
        user_phone: "",
        firstname: "",
        lastname: "",
        email: "",
        country: "",
        country_code: "",
        isNewClient: false,
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTag = () => {
    if (formData.tagInput.trim() && !formData.tags.includes(formData.tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, prev.tagInput.trim()],
        tagInput: "",
      }));
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setShowUserSearch(true);
  };

  const selectUser = async (user: User) => {
    const country = await countriesService.getCountryById(user.country_id);
    setFormData((prev) => ({
      ...prev,
      user_id: user.id,
      user_phone: user.phonenumber || "",
      phone: user.phonenumber || "", // ← Le numéro de l'appel = numéro du user
      firstname: user.first_name || "",
      lastname: user.last_name || "",
      email: user.email || "",
      country: country.name || "",
      country_code: country.code || "",
      client_name: `${user.first_name} ${user.last_name}`,
      isNewClient: false,
    }));
    setSearchQuery("");
    setShowUserSearch(false);
  };

  const handleClearClient = () => {
    setFormData((prev) => ({
      ...prev,
      user_id: "",
      user_phone: "",
      phone: "", // ← On vide aussi le numéro
      firstname: "",
      lastname: "",
      email: "",
      country: "",
      country_code: "",
      client_id: "",
      client_name: "",
      isNewClient: false,
    }));
    setSearchQuery("");
    setShowUserSearch(true);
  };

  const handleCreateClientSuccess = (clientData: {
    firstname: string;
    lastname: string;
    email?: string;
    country?: string;
    country_code?: string;
    phone: string;
  }) => {
    setFormData((prev) => ({
      ...prev,
      firstname: clientData.firstname,
      lastname: clientData.lastname,
      email: clientData.email || "",
      country: clientData.country || "",
      country_code: clientData.country_code || "",
      phone: clientData.phone, // ← Le numéro de l'appel = numéro saisi dans le modal
      client_name: `${clientData.firstname} ${clientData.lastname}`,
      user_id: "",
      user_phone: "",
      isNewClient: true,
    }));
    setShowCreateClientModal(false);
    setSearchQuery("");
    setShowUserSearch(false);
  };

  const toggleTicketCategory = (slug: string) => {
    setTicketData((prev) => ({
      ...prev,
      problem_categories: prev.problem_categories.includes(slug)
        ? prev.problem_categories.filter((s) => s !== slug)
        : [...prev.problem_categories, slug],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      alertError("Veuillez vous connecter");
      return;
    }

    if (!formData.phone) {
      alertError("Veuillez sélectionner ou créer un client avec un numéro de téléphone");
      return;
    }

    const submitData: any = {
      phone: formData.phone,
      direction: formData.direction,
      duration: formData.duration,
      tags: formData.tags,
      notes: formData.notes,
      agent_id: currentUser.id,
      create_ticket: formData.notes && formData.notes.trim().length > 0,
    };

    if (submitData.create_ticket) {
      submitData.ticket_solution = ticketData.solution;
      submitData.ticket_problem_categories = ticketData.problem_categories;
    }

    if (formData.user_id) {
      submitData.user_id = formData.user_id;
      submitData.user_phone = formData.user_phone;
      submitData.firstname = formData.firstname;
      submitData.lastname = formData.lastname;
      submitData.email = formData.email;
      submitData.country = formData.country;
      submitData.country_code = formData.country_code;
    } else if (formData.isNewClient && formData.firstname && formData.lastname) {
      submitData.firstname = formData.firstname;
      submitData.lastname = formData.lastname;
      if (formData.email) submitData.email = formData.email;
      if (formData.country) submitData.country = formData.country;
      if (formData.country_code) submitData.country_code = formData.country_code;
    }

    await onSubmit(submitData);
  };

  const hasLinkedClient = formData.user_id || formData.isNewClient;
  const displayName = formData.client_name || `${formData.firstname} ${formData.lastname}`;
  const isExistingUser = !!formData.user_id;
  const willCreateTicket = formData.notes && formData.notes.trim().length > 0;

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* SECTION LIAISON CLIENT */}
        {!hasLinkedClient && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lier à un compte existant (optionnel)
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Rechercher par nom, email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />

              {showUserSearch && searchQuery.trim() && (
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {usersLoading && <div className="text-center py-4 text-gray-500">Chargement...</div>}

                  {!usersLoading &&
                    filteredUsers.length > 0 &&
                    filteredUsers.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => selectUser(user)}
                        className="w-full text-left p-3 hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <div className="font-medium">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        <div className="text-sm text-gray-500">Tel: {user.phonenumber}</div>
                      </button>
                    ))}

                  {!usersLoading && filteredUsers.length === 0 && (
                    <div className="p-4 text-center">
                      <p className="text-gray-500 mb-2">Aucun utilisateur trouvé</p>
                      <button
                        type="button"
                        onClick={() => setShowCreateClientModal(true)}
                        className="flex items-center gap-2 px-4 py-2 mx-auto text-sm text-white bg-green-600 rounded-lg hover:bg-green-700"
                      >
                        <UserPlus size={16} />
                        Créer un nouveau client
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* BLOC CLIENT LIÉ */}
        {hasLinkedClient && (
          <div
            className={`p-4 rounded-lg border ${isExistingUser ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200"}`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded ${isExistingUser ? "bg-green-200 text-green-800" : "bg-blue-200 text-blue-800"}`}
                  >
                    {isExistingUser ? "Compte existant" : "Nouveau client"}
                  </span>
                </div>
                <div className="font-medium text-gray-900">{displayName}</div>
                {formData.email && <div className="text-sm text-gray-600">Email: {formData.email}</div>}
                {formData.country && <div className="text-sm text-gray-600">Pays: {formData.country}</div>}
                <div className="text-sm text-gray-600">Téléphone: {formData.phone}</div>
              </div>
              <button type="button" onClick={handleClearClient} className="text-red-500 hover:text-red-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Direction */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
          <select
            name="direction"
            value={formData.direction}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="entrant">Appel entrant (client a appelé)</option>
            <option value="sortant">Appel sortant (agent a appelé)</option>
          </select>
        </div>

        {/* Durée */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Durée (secondes)</label>
          <input
            type="number"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            placeholder="120"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={formData.tagInput}
              onChange={(e) => setFormData((prev) => ({ ...prev, tagInput: e.target.value }))}
              placeholder="paiement, litige, facture..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
            />
            <button
              type="button"
              onClick={handleAddTag}
              className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 rounded-full">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Préoccupation du client</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Résumé de la conversation..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>

        {/* Solution */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reponses apportées</label>
          <textarea
            value={ticketData.solution}
            onChange={(e) => setTicketData((prev) => ({ ...prev, solution: e.target.value }))}
            rows={4}
            placeholder="Solution apportée au problème..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>

        {/* SECTION TICKET (si création automatique) */}
        {willCreateTicket && (
          <div className="border-t border-gray-200 pt-4 mt-2">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Informations du ticket (créé automatiquement)</h3>

            {/* Catégories du problème */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégories du problème</label>
              <div className="flex flex-wrap gap-2">
                {problemCategoriesList.map((cat) => (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => toggleTicketCategory(cat.slug)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      ticketData.problem_categories.includes(cat.slug)
                        ? "bg-green-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="submit"
            disabled={isLoading || !formData.phone}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {isLoading ? "Enregistrement..." : "Enregistrer l'appel"}
          </button>
        </div>
      </form>

      {/* Modal de création de client */}
      <ClientCreateModal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        onSuccess={handleCreateClientSuccess}
      />
    </>
  );
}
